import { useEffect, useState } from 'react';
import { useReducedMotion } from '../../lib/useReducedMotion';

/**
 * The lines the loader talks through while it waits. Cosmetic, in order, and
 * deliberately not a stage tracker: the run reports one status and no progress,
 * so anything more specific would be a story the server never told.
 */
const STATUS_LINES: readonly string[] = [
  'Checking our list…',
  'Looking for publishers…',
  'Reading feeds…',
  'Checking freshness…',
];

/** How long each line stays up. */
const LINE_INTERVAL_MS = 4000;

/** How often the elapsed clock is redrawn. It counts real seconds. */
const TICK_MS = 1000;

/** mm:ss for a run that has been going this long. */
function elapsedLabel(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

interface FeedCurationProgressProps {
  /** `created_at` of the run being waited on — the server's clock, not ours. */
  startedAt: string;
}

/**
 * A pending curation run, drawn as a dish listening for signal.
 *
 * The wait is 20 to 60 seconds of somebody else's websites being fetched, with
 * no intermediate progress to report, so the honest thing to show is that
 * something is happening and how long it has been happening for. The clock is
 * the real fact on screen; the art and the line above it are company.
 *
 * Sixteen pixels square, drawn as rectangles on a `crispEdges` SVG in the
 * brand's own colours — no image is fetched for it, and it scales without
 * blurring. Under `prefers-reduced-motion` the waves stop blinking (CSS) and
 * the line stops rotating (here), while the clock keeps counting: it is
 * information, not decoration.
 */
export function FeedCurationProgress({ startedAt }: FeedCurationProgressProps) {
  const prefersReducedMotion = useReducedMotion();
  const [lineIndex, setLineIndex] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const timer = window.setInterval(
      () => setLineIndex((current) => (current + 1) % STATUS_LINES.length),
      LINE_INTERVAL_MS,
    );
    return () => window.clearInterval(timer);
  }, [prefersReducedMotion]);

  const started = Date.parse(startedAt);
  const elapsed = Number.isNaN(started) ? null : elapsedLabel(now - started);
  const line = STATUS_LINES[lineIndex] ?? STATUS_LINES[0];

  return (
    <div className="feed-progress">
      {/* Decoration: the sentence below it is what a screen reader is told. */}
      <svg
        className="pixel-dish"
        viewBox="0 0 16 16"
        shapeRendering="crispEdges"
        aria-hidden="true"
        focusable="false"
      >
        {/* Dish face */}
        <g className="pixel-dish__bowl">
          <rect x="4" y="4" width="2" height="1" />
          <rect x="3" y="5" width="3" height="1" />
          <rect x="2" y="6" width="4" height="1" />
          <rect x="2" y="7" width="4" height="1" />
          <rect x="3" y="8" width="3" height="1" />
          <rect x="4" y="9" width="2" height="1" />
        </g>
        {/* Feed horn, mast and base */}
        <g className="pixel-dish__rig">
          <rect x="6" y="6" width="2" height="1" />
          <rect x="4" y="10" width="2" height="3" />
          <rect x="2" y="13" width="6" height="1" />
          <rect x="1" y="14" width="8" height="1" />
        </g>
        {/* Signal, one arc at a time */}
        <rect className="pixel-dish__wave pixel-dish__wave--1" x="9" y="5" width="1" height="4" />
        <rect className="pixel-dish__wave pixel-dish__wave--2" x="11" y="3" width="1" height="8" />
        <rect className="pixel-dish__wave pixel-dish__wave--3" x="13" y="2" width="1" height="10" />
      </svg>

      <div className="feed-progress__body">
        {/* Announced once. The rotating line and the clock below are not, so a
            screen reader is not read a new sentence every second. */}
        <p className="feed-progress__status" role="status">
          Looking for sources. Leave the dialog open.
        </p>
        <p className="feed-progress__line" aria-hidden="true">
          {line}
        </p>
        <p className="feed-progress__meta" aria-hidden="true">
          {elapsed !== null ? <span className="feed-progress__clock">{elapsed}</span> : null}
          <span>Usually 20 to 60 seconds.</span>
        </p>
      </div>
    </div>
  );
}
